import { Badge, Button, Card } from '@ohear/ui'
/**
 * Network test card.
 *
 * Two measurements, run sequentially against the dedicated network-test
 * endpoints (`/api/v1/network-test/ping` and `/api/v1/network-test/upload`):
 *
 *   1. Latency — three GET pings, median wins (more robust than mean
 *      against a single TCP-handshake outlier).
 *   2. Upload throughput — POST a 2 MiB random blob and divide by elapsed.
 *      The body is randomised so intermediaries can't cache the request.
 *
 * Recommendation thresholds: median ping under 100 ms (warn at 250 ms),
 * upload at least 5 Mbps (warn at 2 Mbps). Anything below the warn line
 * is unlikely to keep up with FLAC chunked uploads on a busy household
 * network and the recorder will struggle.
 *
 * Results are persisted to `users.last_device_test_results.network` so
 * the onboarding flow can see "yes, this user has a workable connection"
 * before booking the first session.
 */
import { useState } from 'react'
import { useSaveDeviceTestResults } from '../../lib/api/account'

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.ohear.ai'
const VERSION_OVERRIDE = import.meta.env.VITE_CF_WORKER_VERSION_OVERRIDE as string | undefined
const TEST_BYTES = 2 * 1024 * 1024 // 2 MiB
const PING_SAMPLES = 3
const RECOMMENDED_MBPS = 5
const RECOMMENDED_PING_MS = 100

type Severity = 'pass' | 'warn' | 'fail'
type Phase = 'idle' | 'pinging' | 'uploading' | 'done'

interface Result {
  medianPingMs: number
  uploadMbps: number
}

function classifyPing(ms: number): Severity {
  if (ms < RECOMMENDED_PING_MS) return 'pass'
  if (ms < 250) return 'warn'
  return 'fail'
}
function classifySpeed(mbps: number): Severity {
  if (mbps >= RECOMMENDED_MBPS) return 'pass'
  if (mbps >= 2) return 'warn'
  return 'fail'
}

export function NetworkTest() {
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const saveResults = useSaveDeviceTestResults()

  const run = async () => {
    setError(null)
    setResult(null)
    try {
      setPhase('pinging')
      const medianPingMs = await measureLatency()
      setPhase('uploading')
      const uploadMbps = await measureUpload()
      setResult({ medianPingMs, uploadMbps })
      setPhase('done')

      saveResults.mutate({
        network: {
          medianPingMs: Math.round(medianPingMs),
          uploadMbps: round2(uploadMbps),
          testedAt: new Date().toISOString(),
        },
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network test failed')
      setPhase('idle')
    }
  }

  const pingSev = result ? classifyPing(result.medianPingMs) : null
  const speedSev = result ? classifySpeed(result.uploadMbps) : null
  const verdict: Severity | null = !result
    ? null
    : pingSev === 'fail' || speedSev === 'fail'
      ? 'fail'
      : pingSev === 'warn' || speedSev === 'warn'
        ? 'warn'
        : 'pass'

  const phaseLabel: Record<Phase, string> = {
    idle: '',
    pinging: 'Measuring round-trip time...',
    uploading: `Uploading a ${(TEST_BYTES / 1024 / 1024).toFixed(0)} MB sample...`,
    done: '',
  }

  return (
    <Card title="Network test">
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-[var(--text-2)] m-0">
          We'll ping the API a few times, then upload a small sample to estimate your connection. We
          recommend at least {RECOMMENDED_MBPS} Mbps upload and under {RECOMMENDED_PING_MS} ms
          latency for smooth recording.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            loading={phase === 'pinging' || phase === 'uploading'}
            disabled={phase === 'pinging' || phase === 'uploading'}
            onClick={() => {
              void run()
            }}
          >
            {phase === 'done' ? 'Re-run test' : 'Run network test'}
          </Button>
          {result && verdict ? (
            <Badge qcSeverity={verdict}>
              {verdict === 'pass'
                ? 'Looks good'
                : verdict === 'warn'
                  ? 'Marginal'
                  : 'Below recommended'}
            </Badge>
          ) : null}
          {saveResults.isPending ? (
            <span className="text-[12px] text-[var(--text-2)]" aria-live="polite">
              Saving...
            </span>
          ) : null}
        </div>

        {phase === 'pinging' || phase === 'uploading' ? (
          <p
            aria-live="polite"
            role="status"
            className="text-[13px] text-[var(--text-1)] m-0 tabular-nums"
          >
            {phaseLabel[phase]}
          </p>
        ) : null}

        {error ? <p className="text-[13px] text-[var(--red)] m-0">{error}</p> : null}

        {result && pingSev && speedSev ? (
          <>
            <dl className="grid grid-cols-2 gap-3 text-[13px] mt-2">
              <Stat
                label="Latency (median)"
                value={`${result.medianPingMs.toFixed(0)} ms`}
                severity={pingSev}
              />
              <Stat
                label="Upload"
                value={`${result.uploadMbps.toFixed(2)} Mbps`}
                severity={speedSev}
              />
            </dl>
            <p className="text-[12px] text-[var(--text-2)] m-0">
              {networkAdviceFor(pingSev, speedSev)}
            </p>
          </>
        ) : null}
      </div>
    </Card>
  )
}

function Stat({
  label,
  value,
  severity,
}: {
  label: string
  value: string
  severity: Severity
}) {
  const color =
    severity === 'pass'
      ? 'text-[var(--green)]'
      : severity === 'warn'
        ? 'text-[var(--amber)]'
        : 'text-[var(--red)]'
  return (
    <div>
      <dt className="text-[12px] text-[var(--text-2)] m-0">{label}</dt>
      <dd className={`m-0 font-medium tabular-nums ${color}`}>{value}</dd>
    </div>
  )
}

async function measureLatency(): Promise<number> {
  const samples: number[] = []
  for (let i = 0; i < PING_SAMPLES; i++) {
    const t0 = performance.now()
    const res = await fetch(`${API_BASE}/api/v1/network-test/ping`, {
      cache: 'no-store',
      headers: VERSION_OVERRIDE
        ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
        : undefined,
    })
    if (!res.ok) throw new Error(`Ping failed (HTTP ${res.status})`)
    // Drain the body so the connection is reusable for the next sample.
    await res.json().catch(() => undefined)
    samples.push(performance.now() - t0)
  }
  samples.sort((a, b) => a - b)
  // Median (PING_SAMPLES is odd by construction).
  return samples[Math.floor(samples.length / 2)] as number
}

async function measureUpload(): Promise<number> {
  // Random body so intermediaries don't cache the request. Filling the
  // first 64 KB with random bytes is plenty — the rest is zeros, but no
  // proxy will dedupe a 2 MiB POST in the wild.
  const buf = new Uint8Array(TEST_BYTES)
  crypto.getRandomValues(buf.subarray(0, Math.min(buf.length, 65536)))
  const headers = new Headers({ 'Content-Type': 'application/octet-stream' })
  if (VERSION_OVERRIDE) {
    headers.set('Cloudflare-Workers-Version-Overrides', `ohear-api="${VERSION_OVERRIDE}"`)
  }
  const t0 = performance.now()
  const res = await fetch(`${API_BASE}/api/v1/network-test/upload`, {
    method: 'POST',
    headers,
    body: buf,
  })
  if (!res.ok) throw new Error(`Upload test failed (HTTP ${res.status})`)
  // The server reports back received_bytes in case any was lost in transit.
  const { received_bytes }: { received_bytes: number } = await res.json()
  const elapsedSec = (performance.now() - t0) / 1000
  const bits = received_bytes * 8
  return bits / elapsedSec / 1_000_000
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

function networkAdviceFor(ping: Severity, speed: Severity): string {
  if (speed === 'fail')
    return 'Your upload speed is below what the recorder needs. A wired ethernet connection or a closer Wi-Fi access point usually fixes this.'
  if (ping === 'fail')
    return 'High latency. Sessions may feel laggy when the supervisor is leaving notes — try moving closer to your router.'
  if (speed === 'warn' && ping === 'warn')
    return 'Workable, but a bit tight. If you can switch to ethernet, your sessions will feel snappier.'
  if (speed === 'warn') return 'Upload is on the low side — recordings may take a moment to sync.'
  if (ping === 'warn') return 'Slightly high ping; nothing to worry about for a recording session.'
  return "Your connection is in great shape. We'll remember this for onboarding."
}
